'use client'

import { useParams } from 'next/navigation'
import { useSelector } from 'react-redux'

import useCustomisation, { PagePath } from '@/hooks/useCustomisation'
import useTracker from '@/hooks/useTracker'
import { selectDetailMenuSite } from '@/redux/meta'
import { useSignupNewsletterMutation } from '@/services/local'
import { ReactNode } from 'react'
import BasicContent from './BasicContent'
import CustomisationContent from './CustomisationContent'

const Footer = ({ pageName }: { pageName: PagePath }): ReactNode => {
  const params = useParams()
  const { notifyNewsletterCompleteRegistration } = useTracker()

  const metaState = useSelector(selectDetailMenuSite)
  const { previewMode, sectionSettingData } = useSelector(
    (state: any) => state['@customisation'].data
  )
  const [signupNewsletter] = useSignupNewsletterMutation()
  const { pageOptions } = useCustomisation(pageName)

  if (pageOptions.withoutFooter) {
    return null
  }

  const {
    settings = {},
    blocksOrder,
    blocks,
  } = (sectionSettingData?.sections?.['footer'] || {}) as {
    settings: Record<string, string>
    blocksOrder: string[]
    blocks: Record<
      string,
      {
        id: string
        type: string
        settings: {
          bodyText: string
          headingsText: string
          requestSubscriberName: boolean
          successMessageText: string
          facebookUrl: string
          instagramUrl: string
          twitterUrl: string
          youtubeUrl: string
        }
      }
    >
  }
  const { footerBackgroundColor, navigationLinkFontColor } = settings

  const handleSubmitNewsletter = async (params: {
    email: string
    firstName: string
    lastName: string
  }) => {
    const response = await signupNewsletter({
      ...params,
      menuId: metaState?.menu?.id,
    })
    notifyNewsletterCompleteRegistration(params)
    return response
  }

  return (
    <div
      id="oddle-shop-footer"
      className="bg-background p-6"
      style={
        {
          '--color-background-color': footerBackgroundColor,
          '--color-headings-font': navigationLinkFontColor,
          '--color-body-font': navigationLinkFontColor,
          '--button-color': navigationLinkFontColor,
          '--color-lines-and-borders': navigationLinkFontColor,
        } as React.CSSProperties
      }
    >
      <div className="my-6 mb-0 mt-0 block w-full p-0">
        <div className="mx-auto max-w-[calc(100vw-16px)] px-0 md:px-4 lg:max-w-[1200px] lg:px-6">
          <CustomisationContent
            blocksData={
              blocksOrder
                ?.map((blockId) => blocks[blockId])
                ?.filter(
                  (block): block is NonNullable<typeof block> =>
                    block !== undefined
                ) || []
            }
            previewMode={previewMode}
            newsletterSignupAction={handleSubmitNewsletter}
          />
          <BasicContent
            previewMode={previewMode}
            locales={metaState?.locales || []}
            locale={params?.locale as string}
          />
        </div>
      </div>
    </div>
  )
}

export default Footer
